/**
 * Which claims get credited.
 *
 * The original tracked "credited victories" and nothing this project has
 * decoded says how it decided one. What both air services actually did in
 * 1915–18 is well established and simple enough to model honestly: a claim
 * needed either **independent witnesses** or a **findable wreck on your own
 * side of the lines**. The German service leaned on the wreck (which is why
 * so many German claims are over their own territory) and the British on the
 * witness, but both accepted either, and this port applies the same pair of
 * tests to both sides rather than inventing a per-nation rule the sources do
 * not support.
 *
 * **The simplification, stated:** "witnessed" here means *another friendly
 * aeroplane was still flying in this fight when it went down* — not a modelled
 * line of sight, not an infantry observer on the ground, not a post-flight
 * squadron report. So a lone pilot who downs a scout deep over the lines gets
 * an unconfirmed claim, exactly as he historically would, while the same kill
 * with a wingman up is credited. Unconfirmed claims are recorded on the pilot's
 * record and never counted toward victories, medals or the score.
 */

import type { VictoryRecord } from "../mission/kill-credit.ts";
import { NO_VICTORIES, type VictoryTally } from "./career-state.ts";

export interface VictoryClaim {
  readonly record: VictoryRecord;
  readonly confirmed: boolean;
  /** Why, in the words a squadron adjutant would use. */
  readonly reason: string;
}

/** The rule in one sentence, for a screen that owes the player an explanation. */
export const CONFIRMATION_RULE =
  "A claim is credited if another aeroplane of your flight was still up to witness it, or the wreck fell on our side of the lines. Anything else is recorded as unconfirmed.";

function reasonFor(record: VictoryRecord): { readonly confirmed: boolean; readonly reason: string } {
  if (record.witnesses > 0) {
    const plural = record.witnesses === 1 ? "one of your flight" : `${record.witnesses} of your flight`;
    return { confirmed: true, reason: `Witnessed by ${plural}.` };
  }
  if (record.overFriendlyTerritory) {
    return { confirmed: true, reason: "The wreck came down on our side of the lines." };
  }
  return { confirmed: false, reason: "Nobody was up to see it and the wreck fell behind enemy lines." };
}

export function creditVictories(records: readonly VictoryRecord[]): readonly VictoryClaim[] {
  return records.map((record) => ({ record, ...reasonFor(record) }));
}

export interface ClaimSummary {
  readonly credited: VictoryTally;
  readonly unconfirmed: number;
}

export function summariseClaims(claims: readonly VictoryClaim[]): ClaimSummary {
  let credited: VictoryTally = NO_VICTORIES;
  let unconfirmed = 0;
  for (const claim of claims) {
    if (!claim.confirmed) {
      unconfirmed += 1;
      continue;
    }
    const kind = claim.record.kind;
    credited = {
      aircraft: credited.aircraft + (kind === "aircraft" ? 1 : 0),
      balloons: credited.balloons + (kind === "balloons" ? 1 : 0),
      zeppelins: credited.zeppelins + (kind === "zeppelins" ? 1 : 0),
    };
  }
  return { credited, unconfirmed };
}

export function addTallies(left: VictoryTally, right: VictoryTally): VictoryTally {
  return {
    aircraft: left.aircraft + right.aircraft,
    balloons: left.balloons + right.balloons,
    zeppelins: left.zeppelins + right.zeppelins,
  };
}

export function tallyTotal(tally: VictoryTally): number {
  return tally.aircraft + tally.balloons + tally.zeppelins;
}
